/**
 * ATL-1694 — carrying an AI-assistant connection request across sign-in.
 *
 * When someone presses "Connect" in Claude or ChatGPT, the Atlas backend's OAuth authorize endpoint
 * sends the browser to `https://web.atlas-app.org/connect-ai?request=<uuid>`. Two things can lose
 * that id before the consent screen reads it:
 *
 *  - the person is signed out: the auth stack has no ConnectAi route, React Navigation drops the
 *    path, and the URL becomes `/login` before anyone has seen it;
 *  - `linkingConfig.getPathFromState` strips the query string, so a reload of `/connect-ai` carries
 *    no `request` at all.
 *
 * So the id is captured from `window.location` once, at module load (before NavigationContainer
 * mounts and rewrites the URL), and kept in `sessionStorage` — per tab, gone when the tab closes,
 * which is the lifetime of one OAuth round trip. After sign-in the navigation container opens the
 * consent screen (see `connectAiRedirectStep`). Native apps never receive this URL (the authorize
 * redirect targets the web app), so on native every function here is a no-op.
 *
 * The id is not a secret: it names a pending request that only an authenticated admin of the
 * organization can decide, and the backend re-checks everything on approve.
 */

export const CONNECT_AI_PATH = 'connect-ai'
export const CONNECT_AI_QUERY_PARAM = 'request'
export const CONNECT_AI_STORAGE_KEY = 'atlas.connectAi.pendingRequest'

/**
 * How long a captured id may still redirect a signed-in person to the consent screen: the same 30
 * minutes the backend keeps the request open (`AUTHORIZATION_REQUEST_TTL_MS`), so a slow sign-in
 * still comes back to a request that can be approved. It also stops a stale id from hijacking a
 * later visit to Home in the same tab.
 */
export const CONNECT_AI_PENDING_TTL_MS = 30 * 60 * 1000

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface KeyValueStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface LocationLike {
  pathname: string
  search: string
}

export const isConnectAiRequestId = (value: unknown): value is string =>
  typeof value === 'string' && UUID_RE.test(value)

/** The request id, when this location is the consent page carrying one; otherwise `null`. */
export const parseConnectAiRequestId = (location: LocationLike | null | undefined): string | null => {
  if (!location || typeof location.pathname !== 'string') return null
  const path = location.pathname.replace(/\/+$/, '')
  if (!path.endsWith(`/${CONNECT_AI_PATH}`)) return null
  let id: string | null
  try {
    id = new URLSearchParams(location.search ?? '').get(CONNECT_AI_QUERY_PARAM)
  } catch {
    return null
  }
  return isConnectAiRequestId(id) ? id.toLowerCase() : null
}

interface StoredRequest {
  id: string
  capturedAt: number
}

const parseStored = (raw: string | null): StoredRequest | null => {
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object') return null
    const { id, capturedAt } = value as { id?: unknown; capturedAt?: unknown }
    if (!isConnectAiRequestId(id) || typeof capturedAt !== 'number' || !Number.isFinite(capturedAt)) return null
    return { id, capturedAt }
  } catch {
    return null
  }
}

/**
 * `sessionStorage`, or `null` where there is none (native) or the browser refuses it (some private
 * modes throw on access). Every caller treats `null` as "nothing pending".
 */
export const browserSessionStorage = (): KeyValueStorage | null => {
  try {
    const storage = (globalThis as { sessionStorage?: KeyValueStorage }).sessionStorage
    return storage ?? null
  } catch {
    return null
  }
}

const browserLocation = (): LocationLike | null => {
  try {
    const location = (globalThis as { location?: LocationLike }).location
    return location && typeof location.pathname === 'string' ? location : null
  } catch {
    return null
  }
}

/** Remember the request this page was opened with. Returns the id it stored, or `null`. */
export const captureConnectAiRequest = (
  location: LocationLike | null = browserLocation(),
  storage: KeyValueStorage | null = browserSessionStorage(),
  now: number = Date.now()
): string | null => {
  const id = parseConnectAiRequestId(location)
  if (!id || !storage) return id
  try {
    storage.setItem(CONNECT_AI_STORAGE_KEY, JSON.stringify({ id, capturedAt: now }))
  } catch {
    // Storage full or refused: the consent screen still reads the id from the route while the URL
    // carries it; only the signed-out round trip loses it, and the person restarts from Claude.
  }
  return id
}

/** The remembered request id, or `null` when none is remembered or it is older than the TTL. */
export const readPendingConnectAiRequest = (
  storage: KeyValueStorage | null = browserSessionStorage(),
  now: number = Date.now()
): string | null => {
  if (!storage) return null
  let stored: StoredRequest | null
  try {
    stored = parseStored(storage.getItem(CONNECT_AI_STORAGE_KEY))
  } catch {
    return null
  }
  if (!stored) return null
  if (now - stored.capturedAt > CONNECT_AI_PENDING_TTL_MS || stored.capturedAt - now > 60_000) {
    clearPendingConnectAiRequest(storage)
    return null
  }
  return stored.id
}

export const clearPendingConnectAiRequest = (storage: KeyValueStorage | null = browserSessionStorage()): void => {
  if (!storage) return
  try {
    storage.removeItem(CONNECT_AI_STORAGE_KEY)
  } catch {
    // Nothing to do: an entry that cannot be removed still expires by its TTL.
  }
}

/** Which request the consent screen should show: the route's own param first, then the remembered one. */
export const resolveConnectAiRequestId = (
  routeParam: unknown,
  pending: string | null
): string | null => (isConnectAiRequestId(routeParam) ? routeParam.toLowerCase() : pending)

/**
 * Remember a request the consent screen received as a route param, so a reload of `/connect-ai`
 * (whose query the linking map strips) still finds it.
 */
export const rememberConnectAiRequest = (
  id: string,
  storage: KeyValueStorage | null = browserSessionStorage(),
  now: number = Date.now()
): void => {
  if (!storage || !isConnectAiRequestId(id)) return
  try {
    storage.setItem(CONNECT_AI_STORAGE_KEY, JSON.stringify({ id: id.toLowerCase(), capturedAt: now }))
  } catch {
    // See captureConnectAiRequest.
  }
}

/** The part of a React Navigation root state the redirect decision reads. */
export interface RootNavigationStateLike {
  routeNames?: readonly string[]
  routes: ReadonlyArray<{ name: string }>
}

/**
 * What the navigation container should do about a remembered request, given its COMMITTED root
 * state — read from `onReady` / `onStateChange`, never from a screen's mount effect: when the
 * signed-in stack mounts into a container that already rendered the auth stack, the new navigator
 * commits its initial `[Home]` state in its own effect, after its children's effects, and so
 * overwrites a `navigate` that Home issued on mount (@react-navigation/core useNavigationBuilder,
 * the `!getIsInitial()` → `setState(nextState)` effect). That is the sign-in path, i.e. the common one.
 *
 *  - `'open'`    — a request is pending, the root stack can show the consent screen, it is not
 *                  showing it, and this request has not been opened before in this page load;
 *  - `'showing'` — the consent screen is already in the stack (a signed-in person opened
 *                  `/connect-ai` directly): record the id as handled so leaving the screen —
 *                  whose card can outlive its route during the closing transition, together with
 *                  the id it re-remembers — never reopens it;
 *  - `'none'`    — nothing to do: nothing pending, already handled, or a root stack without the
 *                  consent screen (signed out, or the paywall-only stack).
 */
export type ConnectAiRedirectStep = 'open' | 'showing' | 'none'

/**
 * Whether a committed root state has no consent screen to show — nobody signed in (or the
 * paywall-only stack). The container then forgets which requests it has handled: "opened at most
 * once per page load" protects the person who LEFT the screen, and a sign-out ends that person's
 * session. The next one to sign in — the administrator the not-admin message sends for — must be
 * brought to the request the non-admin could not decide.
 */
export const consentStackAbsent = (rootState: RootNavigationStateLike | null | undefined): boolean =>
  !!rootState && !rootState.routeNames?.includes('ConnectAi')

export const connectAiRedirectStep = (
  pending: string | null,
  rootState: RootNavigationStateLike | null | undefined,
  handled: ReadonlySet<string>
): ConnectAiRedirectStep => {
  if (pending === null || !rootState) return 'none'
  if (rootState.routes.some((route) => route.name === 'ConnectAi')) return 'showing'
  if (handled.has(pending) || !rootState.routeNames?.includes('ConnectAi')) return 'none'
  return 'open'
}
